import { Injectable } from '@angular/core';
import {
	CanActivate,
	ActivatedRouteSnapshot,
	RouterStateSnapshot,
	Router
} from '@angular/router';
import { RcService } from '../services/rc.service';
import { AuthService } from '../services/auth.service';
import { ToastrService } from 'ngx-toastr';

@Injectable({
	providedIn: 'root'
})
export class RcGuard implements CanActivate {
	private lastValidatedRc: string | null = null;
	private readonly validationCache = new Map<string, boolean>();

	constructor(
		private readonly rcService: RcService,
		private readonly authService: AuthService,
		private readonly router: Router,
		private readonly toastr: ToastrService
	) {}

	canActivate(
		route: ActivatedRouteSnapshot,
		state: RouterStateSnapshot
	): boolean {
		// Check if we're on localhost
		const isLocalhost =
			globalThis.location.hostname.includes('localhost') ||
			globalThis.location.hostname.includes('127.0.0.1');
		if (isLocalhost || globalThis.location.hostname === 'rms.katdev.com') {
			console.log('RcGuard: Running on localhost, bypassing RC check');
			return true;
		}

		// Get RC from the route parameters
		const rcParam = route.params['rc'];

		// Check if user is trying to change RC while logged in
		// Each RC has its own database and users, so switching RC requires logout
		if (this.authService.isAuthenticated() && rcParam) {
			const currentStoredRc = this.rcService.getCurrentRc();

			// If user is logged in and trying to access a different RC than the one stored
			if (currentStoredRc && currentStoredRc !== rcParam) {
				console.log(
					'RcGuard: User attempting to change RC while authenticated. Current RC:',
					currentStoredRc,
					'Requested RC:',
					rcParam
				);

				this.toastr.warning(
					'You have been logged out due to RC change.',
					'RC Change Detected'
				);

				// Clear user data and logout
				this.authService.clearUserData();

				// Clear RC validation cache since user is logging out
				this.clearValidationCache();

				// Redirect to the new RC's login page
				this.router.navigate([`/${rcParam}/auth/login`], {
					replaceUrl: true
				});
				return false;
			}
		}

		// If no RC in the route, redirect to select-rc only once
		if (!rcParam) {
			console.log(
				'RcGuard: No RC parameter found, redirecting to /select-rc'
			);
			// Use replace to avoid adding to history stack
			this.router.navigate(['/select-rc'], { replaceUrl: true });
			return false;
		}

		// Use cached validation if available and RC hasn't changed
		if (
			this.lastValidatedRc === rcParam &&
			this.validationCache.has(rcParam)
		) {
			const isValid = this.validationCache.get(rcParam)!;
			if (isValid) {
				return true;
			} else {
				this.toastr.error('Invalid RC selected');
				this.router.navigate(['/select-rc'], { replaceUrl: true });
				return false;
			}
		}

		// Check if the RC is valid
		const isValidRc = this.rcService.isValidRc(rcParam);

		// Cache the validation result
		this.validationCache.set(rcParam, isValidRc);
		this.lastValidatedRc = rcParam;

		if (!isValidRc) {
			console.log('RcGuard: Invalid RC:', rcParam);
			this.toastr.error('Invalid RC selected');
			this.router.navigate(['/select-rc'], { replaceUrl: true });
			return false;
		}

		// Set the current RC in the service only if it's different
		const currentRc = localStorage.getItem('currentRc');
		if (currentRc !== rcParam) {
			console.log('RcGuard: Setting current RC:', rcParam);
			this.rcService.setRc(rcParam);
			localStorage.setItem('currentRc', rcParam);
		}

		return true;
	}

	// Method to clear cache when needed
	clearValidationCache(): void {
		this.validationCache.clear();
		this.lastValidatedRc = null;
	}
}
